import {
  BmarkHedgeOrdersTableDefinitionFactory,
  BmarkSpotRiskTableDefinitionFactory,
} from "shared/cubiq-services";

import { Component, ViewEncapsulation, ViewChild, ElementRef, OnInit } from "@angular/core";
import { BfxBlotterFilterService } from "./bfx-blotter-filter.service";
import {
  BfxBlotterFilterId,
  FilterType,
  ExtendedOrderTypeIdentifier,
} from "./bfx-blotter-filter.constants";
import { BlotterType } from "shared/cubiq-model-multi-asset";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import {
  BaseTableDefinitionFactory,
  BmarkClientOrdersTableDefinitionFactory,
} from "shared/cubiq-services";
import { DashboardItemType } from "../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../golden-layout/golden-layout-addition-mode";
import { GoldenLayoutPopout } from "app/golden-layout/golden-layout-popout";
import { BfxBlotterFilterListComponent } from "./bfx-blotter-filter-list.component";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";

import { AlgoMonitorDefinitionFactory } from "shared/cubiq-services/model/AlgoMonitorDefinitionFactory";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Component({
  selector: ".bfx-blotter-filter-manager",
  templateUrl: "./html/bfx-blotter-filter-manager.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BfxBlotterFilterManagerComponent implements GoldenLayoutComponent, OnInit {
  public dividerPosition: number = 570.5;
  public blotterId: string = null;
  public dividerDragInProgress: boolean = false;
  public blotterTypeId: BfxBlotterFilterId = null;
  public filterType: FilterType = null;
  public extendedTypeId: ExtendedOrderTypeIdentifier;

  public showConfirmDialog: boolean = false;
  public showMessageDialog: boolean = false;

  public showNewDialog: boolean = false;
  public newFilterName: string = "";
  public newFilterCallback: any;

  public confirmMessage: string = "";
  public confirmCallback: any = null;

  public message: string = null;
  public messageIcon: string = null;
  public messageTitle: string = null;

  public multiSortMeta: any;

  private curConfig: any;
  public parentPopout: GoldenLayoutPopout = null;
  public persistEmbeddedComponentConfigData: boolean = true;
  public updateLayoutCallback: any;

  @ViewChild("rootElement", { static: true }) protected element: ElementRef;
  @ViewChild("filterNameElement", { static: true })
  protected newFilterNameElement: ElementRef;
  @ViewChild("filterList", { static: true })
  protected filterList: BfxBlotterFilterListComponent;

  constructor(protected blotterService: BfxBlotterFilterService) {}

  ngOnInit() {
    this.blotterService.isDialogOpenMode.subscribe(
      (isOnOpenDilaogMode) => (this.parentPopout.disabledClose = isOnOpenDilaogMode),
    );
  }

  ngAfterContentInit() {
    //Called after ngOnInit when the component's or directive's content has been initialized.
    //Add 'implements AfterContentInit' to the class.
    this.blotterService.initTableDefinition(
      this.blotterTypeId,
      this.filterType,
      this.blotterId,
      this.extendedTypeId,
    );
  }

  public restoreFromConfiguration(config: any): void {
    this.curConfig = config;
    setTimeout(() => {
      this.multiSortMeta = config.multiSortMeta
        ? config.multiSortMeta
        : [
            { field: "favorite", order: -1 },
            { field: "filterName", order: 1 },
          ];
      this.filterList.resort();
    }, 0);
  }

  public glInit(data: any, container: any, state: any): void {
    this.blotterId = data.blotterId;
    this.blotterTypeId = data.blotterTypeId;
    this.filterType = data.filterType;
    this.extendedTypeId = data.extendedTypeId;
    this.blotterService.registerFilterManager(
      this,
      this.blotterTypeId.blotterType,
      this.blotterTypeId.assetClass,
      this.extendedTypeId,
    );
  }

  public getComponentConfiguration(): any {
    if (this.curConfig["multiSortMeta"] == null) {
      this.curConfig["multiSortMeta"] = {};
    }
    Object.assign(this.curConfig["multiSortMeta"], this.filterList.getAppliedSort());
    return this.curConfig;
  }

  public setLayoutUpdatedCallback?(callBack: any): void {
    this.updateLayoutCallback = callBack;
  }

  onClose(): boolean {
    if (this.updateLayoutCallback) {
      this.parentPopout.embeddedComponentConfig.data["multiSortMeta"] =
        this.filterList.getAppliedSort();
      this.updateLayoutCallback();
    }
    return true;
  }

  public dividerStart(event: any): void {
    this.dividerDragInProgress = true;
  }

  public dividerDrag(event: any): void {
    if (this.dividerDragInProgress) {
      this.dividerPosition = event.clientX - this.element.nativeElement.offsetParent.offsetLeft;
    }
  }

  public dividerEnd(event: any): void {
    this.dividerDragInProgress = false;
  }

  setParentPopup(parentPopout: GoldenLayoutPopout) {
    this.parentPopout = parentPopout;
    parentPopout.setShowDock(false);
  }

  public static openFilterManager(
    tableDefinition: BaseTableDefinitionFactory,
    translationService: TranslateService,
    layoutService: GoldenLayoutService,
    filterType: FilterType = FilterType.BlotterFilter,
    blotterId: string = null,
    sourceEvent: Event,
  ): void {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();

    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;
    config.preferredSize = { width: 1138, height: 700 };
    config.minimumSize = { width: 1138, height: 542 };
    config.sourceEvent = event;

    let blotterType: BlotterType = tableDefinition.getBlotterType();
    let extendedType =
      tableDefinition instanceof BmarkClientOrdersTableDefinitionFactory ||
      tableDefinition instanceof BmarkHedgeOrdersTableDefinitionFactory ||
      tableDefinition instanceof BmarkSpotRiskTableDefinitionFactory
        ? ExtendedOrderTypeIdentifier.BENCHMARK
        : ExtendedOrderTypeIdentifier.OTHER;

    if (tableDefinition instanceof AlgoMonitorDefinitionFactory) {
      extendedType = ExtendedOrderTypeIdentifier.ALGO;
    }

    config.data = {
      blotterTypeId: new BfxBlotterFilterId(blotterType, tableDefinition.getAssetClass()),
      filterType: filterType,
      blotterId: blotterId,
      extendedTypeId: extendedType,
      // sortModel: BfxBlotterFilterManagerComponent.sortModel
    };
    let title: string =
      translationService.instant("filter.manager.title") +
      this.getBlotterTypeStr(tableDefinition, blotterType);
    layoutService.launchFromDashboard(
      title,
      DashboardItemType.FilterManager,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  private static getBlotterTypeStr(
    tableDefinition: BaseTableDefinitionFactory,
    bt: BlotterType,
  ): string {
    let str = this.customCapitalize([BlotterType[tableDefinition.getBlotterType()]].toString());
    if (BlotterType.ORDER == bt) {
      if (tableDefinition instanceof BmarkClientOrdersTableDefinitionFactory) {
        str = " Benchmark Summary";
      } else if (tableDefinition instanceof AlgoMonitorDefinitionFactory) {
        str = " Algo Monitor";
      } else {
        str += " Blotter/Ladder";
      }
    } else {
      str += " Blotter";
    }

    return str;
  }

  private static customCapitalize(blotterType: string): string {
    if (blotterType === "RISKTRADE") {
      return "Risk Trade";
    }
    return blotterType.charAt(0) + blotterType.slice(1).toLowerCase();
  }
}
